import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FiFileText,
  FiPlus,
  FiSearch,
  FiDownload,
  FiEye,
  FiUser,
  FiClipboard,
  FiCalendar,
  FiX,
  FiSave,
  FiAlertCircle,
} from "react-icons/fi";
import "../../../assets/css/doctor/doctorpages/Prescriptions.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";

const Prescriptions = () => {
  const { baseUrl } = useContext(AppContext);
  const token = localStorage.getItem("doctor_token");
  const authHeaders = { Authorization: `Bearer ${token}`, Accept: "application/json" };

  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Completed appointments without a prescription yet — for the "Create" dropdown
  const [completedAppointments, setCompletedAppointments] = useState([]);

  const [newPrescription, setNewPrescription] = useState({
    appointment_id: "",
    content: "",
  });
  const [saving, setSaving] = useState(false);

  // ── Fetch prescriptions ──────────────────────────────────────
  const fetchPrescriptions = useCallback(() => {
    setLoading(true);
    axios.get(`${baseUrl}/doctor/Prescription/list`, { headers: authHeaders })
      .then((res) => setPrescriptions(res.data.data || []))
      .catch((err) => console.error("Prescriptions load error:", err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  // ── Fetch completed appointments (for the create dropdown) ──
  const fetchCompletedAppointments = useCallback(() => {
    axios.get(`${baseUrl}/doctor/Bookings/list?status=completed`, { headers: authHeaders })
      .then((res) => setCompletedAppointments(res.data.data || []))
      .catch((err) => console.error("Appointments load error:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  useEffect(() => {
    fetchPrescriptions();
    fetchCompletedAppointments();
  }, [fetchPrescriptions, fetchCompletedAppointments]);

  const stats = {
    total: prescriptions.length,
    thisMonth: prescriptions.filter((p) => {
      const created = new Date(p.created_at);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length,
  };

  const handleCreatePrescription = () => {
    if (!newPrescription.appointment_id || !newPrescription.content.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "Please select an appointment and write the prescription content." });
      return;
    }
    setSaving(true);
    axios.post(`${baseUrl}/doctor/Prescription/create`, newPrescription, { headers: authHeaders })
      .then(() => {
        Swal.fire({ icon: "success", title: "Saved", text: "Prescription created successfully.", timer: 2000, showConfirmButton: false });
        setShowCreateModal(false);
        setNewPrescription({ appointment_id: "", content: "" });
        fetchPrescriptions();
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "Failed", text: err?.response?.data?.message || "Something went wrong." });
      })
      .finally(() => setSaving(false));
  };

  const handleViewPrescription = (prescription) => {
    setSelectedPrescription(prescription);
    setShowViewModal(true);
  };

  const handleDownloadPrescription = (prescription) => {
    axios.get(`${baseUrl}/doctor/Prescription/${prescription.id}/download`, {
      headers: authHeaders,
      responseType: "blob",
    })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `prescription_${prescription.id}.pdf`;
        link.click();
      })
      .catch(() => {
        Swal.fire({ icon: "error", title: "Failed", text: "Could not download prescription." });
      });
  };

  const filteredPrescriptions = prescriptions.filter((prescription) => {
    const patientName = prescription.patient?.name || "";
    const q = searchTerm.toLowerCase();
    return (
      patientName.toLowerCase().includes(q) ||
      (prescription.content || "").toLowerCase().includes(q) ||
      String(prescription.id).includes(q)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <DoctorMasterLayout>
      <div className="thp-prescriptions-body">
        <div className="thp-prescriptions-container">
          {/* Header */}
          <div className="thp-header">
            <div className="thp-header-left">
              <h2 className="thp-title">Prescriptions Management</h2>
              <p className="thp-subtitle">Create and manage therapy prescriptions for your patients</p>
            </div>
            <button className="thp-btn-create" onClick={() => setShowCreateModal(true)}>
              <FiPlus /> Create New Prescription
            </button>
          </div>

          {/* Stats Cards */}
          <div className="thp-stats-cards">
            <div className="thp-stat-card">
              <div className="thp-stat-icon-box">
                <FiFileText className="thp-stat-icon" />
              </div>
              <div className="thp-stat-info">
                <h3 className="thp-stat-value">{stats.total}</h3>
                <p className="thp-stat-label">Total Prescriptions</p>
              </div>
            </div>

            <div className="thp-stat-card">
              <div className="thp-stat-icon-box">
                <FiCalendar className="thp-stat-icon" />
              </div>
              <div className="thp-stat-info">
                <h3 className="thp-stat-value">{stats.thisMonth}</h3>
                <p className="thp-stat-label">This Month</p>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="thp-table-section">
            <div className="thp-section-head">
              <h3 className="thp-section-title">All Prescriptions</h3>
              <div className="thp-search-box">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Search by patient or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="thp-table-wrapper">
              {loading ? (
                <div className="thp-no-data">Loading prescriptions...</div>
              ) : filteredPrescriptions.length > 0 ? (
                <table className="thp-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Patient</th>
                      <th>Session Date</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrescriptions.map((prescription) => (
                      <tr key={prescription.id}>
                        <td>
                          <div className="thp-id-cell">
                            <FiClipboard /> RX{String(prescription.id).padStart(4, "0")}
                          </div>
                        </td>
                        <td>
                          <div className="thp-patient-cell">
                            <div className="thp-patient-avatar">
                              <FiUser />
                            </div>
                            <span className="thp-patient-name">{prescription.patient?.name || "N/A"}</span>
                          </div>
                        </td>
                        <td>{formatDate(prescription.appointment?.appointment_date)}</td>
                        <td>{formatDate(prescription.created_at)}</td>
                        <td>
                          <div className="thp-actions">
                            <button
                              className="thp-action-btn thp-view"
                              onClick={() => handleViewPrescription(prescription)}
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              className="thp-action-btn thp-download"
                              onClick={() => handleDownloadPrescription(prescription)}
                              title="Download"
                            >
                              <FiDownload />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="thp-no-data">
                  <FiAlertCircle />
                  No prescriptions found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Prescription Modal */}
      {showCreateModal && (
        <div className="thp-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="thp-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="thp-modal-header">
              <h3 className="thp-modal-title">Create New Prescription</h3>
              <button className="thp-modal-close" onClick={() => setShowCreateModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="thp-modal-body">
              <div className="thp-form-group">
                <label htmlFor="appointment_id">
                  <FiUser /> Select Completed Session <span className="thp-required">*</span>
                </label>
                <select
                  id="appointment_id"
                  value={newPrescription.appointment_id}
                  onChange={(e) => setNewPrescription({ ...newPrescription, appointment_id: e.target.value })}
                >
                  <option value="">-- Select Patient / Session --</option>
                  {completedAppointments.map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {apt.patient?.name || "Unknown"} — {formatDate(apt.appointment_date)}
                    </option>
                  ))}
                </select>
                {completedAppointments.length === 0 && (
                  <span className="thp-form-hint">No completed sessions available yet.</span>
                )}
              </div>

              <div className="thp-form-group">
                <label htmlFor="content">
                  <FiClipboard /> Prescription / Notes <span className="thp-required">*</span>
                </label>
                <textarea
                  id="content"
                  rows="8"
                  placeholder="Write diagnosis, exercises, home practice instructions, and any other notes for the patient..."
                  value={newPrescription.content}
                  onChange={(e) => setNewPrescription({ ...newPrescription, content: e.target.value })}
                />
              </div>
            </div>
            <div className="thp-modal-footer">
              <button className="thp-btn-cancel" onClick={() => setShowCreateModal(false)} disabled={saving}>
                Cancel
              </button>
              <button className="thp-btn-save" onClick={handleCreatePrescription} disabled={saving}>
                <FiSave /> {saving ? "Saving..." : "Save Prescription"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Prescription Modal */}
      {showViewModal && selectedPrescription && (
        <div className="thp-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="thp-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="thp-modal-header">
              <h3 className="thp-modal-title">
                Prescription Details - RX{String(selectedPrescription.id).padStart(4, "0")}
              </h3>
              <button className="thp-modal-close" onClick={() => setShowViewModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="thp-modal-body">
              <div className="thp-detail-section">
                <h4 className="thp-detail-heading">Patient Information</h4>
                <div className="thp-detail-grid">
                  <div className="thp-detail-item thp-full">
                    <span className="thp-detail-label">Name</span>
                    <span className="thp-detail-text">{selectedPrescription.patient?.name || "N/A"}</span>
                  </div>
                </div>
              </div>

              <div className="thp-detail-section">
                <h4 className="thp-detail-heading">Prescription / Notes</h4>
                <p className="thp-detail-text thp-notes">{selectedPrescription.content}</p>
              </div>

              <div className="thp-detail-section">
                <h4 className="thp-detail-heading">Session Information</h4>
                <div className="thp-detail-grid">
                  <div className="thp-detail-item">
                    <span className="thp-detail-label">Session Date</span>
                    <span className="thp-detail-text">{formatDate(selectedPrescription.appointment?.appointment_date)}</span>
                  </div>
                  <div className="thp-detail-item">
                    <span className="thp-detail-label">Created On</span>
                    <span className="thp-detail-text">{formatDate(selectedPrescription.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="thp-modal-footer">
              <button className="thp-btn-cancel" onClick={() => setShowViewModal(false)}>
                Close
              </button>
              <button
                className="thp-btn-download"
                onClick={() => handleDownloadPrescription(selectedPrescription)}
              >
                <FiDownload /> Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </DoctorMasterLayout>
  );
};

export default Prescriptions;